
import { useEffect, useRef, useState } from "react";
import { useTranslations } from "use-intl";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/utils/cn";
import { CONTENT_LANGUAGES } from "@examora/shared/languages/content-languages";
import { getContentLanguageCookie, setContentLanguageCookie } from "@/utils/content-language";

/**
 * Replaces `LocaleSwitcher` in the site header for logged-in students (see
 * `SiteHeader`). This dropdown does NOT change the interface language
 * anymore — that moved to the student's own Profile settings, since it's
 * now a per-account preference (`profiles.uiLocale`, edited there). This
 * one instead answers "which language's mocks do I want to see" and
 * filters the /exams catalog — the two were conflated into one control
 * before, which is exactly what the user asked to untangle.
 *
 * Only en/tr are offered (see content-languages.ts) — those are the two
 * exam programs that currently have content.
 *
 * A custom popover rather than a plain `<select>` so each option can show
 * its flag next to the language name and the trigger itself reads as
 * "current flag + name" instead of a bare browser-styled dropdown — plain
 * `<select>` markup can't render an emoji/label pairing consistently across
 * browsers' native option rendering.
 *
 * Reads/writes a cookie rather than the interface locale/URL, so it works
 * the same way regardless of which interface language the student has
 * chosen, and persists across page loads without a schema change.
 */
export function MockLanguageSwitcher() {
  const t = useTranslations("common.mockLanguage");
  // null until mounted — the cookie is client-only, so guessing during SSR
  // risks a hydration mismatch (same reasoning as ThemeToggle).
  const [value, setValue] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- resolving the client-only cookie value on mount, mirrors ThemeToggle
    setValue(getContentLanguageCookie());
  }, []);

  useEffect(() => {
    function handleClick(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  if (value === null) {
    return <div className="h-9 w-[5.5rem]" aria-hidden="true" />;
  }

  const current = CONTENT_LANGUAGES.find((lang) => lang.code === value) ?? CONTENT_LANGUAGES[0];

  function handleSelect(code: (typeof CONTENT_LANGUAGES)[number]["code"]) {
    setValue(code);
    setContentLanguageCookie(code);
    setOpen(false);
  }

  return (
    <div ref={ref} className="relative">
      <span className="sr-only">{t("switchTo")}</span>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={t("switchTo")}
        className={cn(
          "flex h-9 items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 text-sm text-foreground transition-colors hover:border-primary/40",
          open && "border-primary/50 ring-2 ring-ring",
        )}
      >
        <img src={current.flagSrc} alt="" aria-hidden="true" className="h-3.5 w-5 shrink-0 rounded-[2px] object-cover shadow-sm ring-1 ring-black/10" />
        <span className="hidden sm:inline">{current.label}</span>
        <ChevronDown
          className={cn("h-3.5 w-3.5 text-muted-foreground transition-transform", open && "rotate-180")}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={t("switchTo")}
          className="absolute right-0 top-full z-50 mt-1.5 min-w-[9.5rem] overflow-hidden rounded-lg border border-border bg-card py-1 shadow-lg animate-scale-in"
        >
          {CONTENT_LANGUAGES.map((lang) => {
            const selected = lang.code === value;
            return (
              <button
                key={lang.code}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => handleSelect(lang.code)}
                className={cn(
                  "flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors hover:bg-muted",
                  selected && "bg-primary/10 font-medium text-primary",
                )}
              >
                <img src={lang.flagSrc} alt="" aria-hidden="true" className="h-3.5 w-5 shrink-0 rounded-[2px] object-cover shadow-sm ring-1 ring-black/10" />
                <span className="flex-1">{lang.label}</span>
                {selected && <Check className="h-3.5 w-3.5" aria-hidden="true" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
